import {FC, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    useAdminBatchJobDetail,
    useAdminBatchJobs,
    useCancelAdminBatchJob,
} from '../../hooks/useAdminBatch.ts';
import {AdminBatchJobStatus, AdminBatchJobType, AdminBatchScopeType} from '../../types/adminBatch.ts';
import {
    batchJobStatusColor,
    batchJobStatusLabel,
    batchJobTypeLabel,
    batchScopeLabel,
    formatBatchDateTime,
} from '../../utils/batchUi.ts';
import {BatchJobDetailPanel} from './detail/BatchJobDetailPanel.tsx';

type BatchResultInfo = {
    phase?: string;
    currentProvider?: string;
    heartbeatAt?: string;
    failureSummary?: {
        providerFailedBatches?: number;
        internalFailedBatches?: number;
        retrySuppressedBatches?: number;
        retriedWithFallbackBatches?: number;
    };
};

const parseResultJson = (resultJson?: string): BatchResultInfo => {
    if (!resultJson) return {};
    try {
        const parsed = JSON.parse(resultJson) as BatchResultInfo;
        return {
            phase: parsed.phase,
            currentProvider: parsed.currentProvider,
            heartbeatAt: parsed.heartbeatAt,
            failureSummary: parsed.failureSummary,
        };
    } catch {
        return {};
    }
};

export const AdminBatchJobList: FC = () => {
    const [searchParams] = useSearchParams();
    const initialJobType = searchParams.get('jobType') || '';
    const initialScopeType = searchParams.get('scopeType') || '';
    const initialScopeId = searchParams.get('scopeId') || '';

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [jobType, setJobType] = useState<string>(initialJobType);
    const [scopeType, setScopeType] = useState<string>(initialScopeType);
    const [status, setStatus] = useState<string>('');
    const [scopeIdText, setScopeIdText] = useState<string>(initialScopeId);
    const [selectedJobId, setSelectedJobId] = useState<number | undefined>(undefined);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [detailSubmissionPage, setDetailSubmissionPage] = useState(0);
    const [detailSubmissionSize, setDetailSubmissionSize] = useState(50);
    const [detailGeneratedPage, setDetailGeneratedPage] = useState(0);
    const [detailGeneratedSize, setDetailGeneratedSize] = useState(100);

    const params = useMemo(() => {
        const scopeIdValue = scopeIdText.trim() === '' ? undefined : Number(scopeIdText);
        return {
            page: page + 1,
            size,
            jobType: jobType || undefined,
            scopeType: scopeType || undefined,
            status: status || undefined,
            scopeId: Number.isFinite(scopeIdValue) ? scopeIdValue : undefined,
        } as {
            page: number;
            size: number;
            jobType?: AdminBatchJobType;
            scopeType?: AdminBatchScopeType;
            status?: AdminBatchJobStatus;
            scopeId?: number;
        };
    }, [page, size, jobType, scopeType, status, scopeIdText]);

    const {data, isLoading, error, refetch: refetchJobs} = useAdminBatchJobs(params);
    const {
        data: detailData,
        isLoading: detailLoading,
        error: detailError,
        refetch: refetchDetail,
    } = useAdminBatchJobDetail(
        selectedJobId,
        {
            submissionPage: detailSubmissionPage,
            submissionSize: detailSubmissionSize,
            generatedWordPage: detailGeneratedPage,
            generatedWordSize: detailGeneratedSize,
        },
        !!selectedJobId,
    );
    const cancelJob = useCancelAdminBatchJob();

    const detailJobAwaitingResults = detailData?.job.status === 'AWAITING_RESULTS';
    const listHasInProgress = (data?.content ?? []).some(
        (job) => job.status === 'PENDING' || job.status === 'RUNNING' || job.status === 'AWAITING_RESULTS',
    );

    useEffect(() => {
        if (!selectedJobId || !data) return;
        const exists = data.content.some((job) => job.id === selectedJobId);
        if (!exists) setSelectedJobId(undefined);
    }, [data, selectedJobId]);

    useEffect(() => {
        setDetailSubmissionPage(0);
        setDetailGeneratedPage(0);
    }, [selectedJobId]);

    useEffect(() => {
        if (!autoRefresh || !listHasInProgress) return;
        const timer = window.setInterval(() => {
            refetchJobs();
        }, 10000);
        return () => window.clearInterval(timer);
    }, [autoRefresh, listHasInProgress, refetchJobs]);

    useEffect(() => {
        if (!autoRefresh || !selectedJobId || !detailJobAwaitingResults) return;
        const timer = window.setInterval(() => {
            refetchDetail();
        }, 60000);
        return () => window.clearInterval(timer);
    }, [autoRefresh, selectedJobId, detailJobAwaitingResults, refetchDetail]);

    return (
        <Box sx={{p: 3}}>
            <Stack direction="row" spacing={1.5} sx={{mb: 3}} alignItems="center" justifyContent="space-between">
                <Typography variant="h4">배치 작업 통합 이력</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <FormControlLabel
                        sx={{mr: 0.5}}
                        control={(
                            <Switch
                                size="small"
                                checked={autoRefresh}
                                onChange={(_, checked) => setAutoRefresh(checked)}
                            />
                        )}
                        label="자동 모니터링"
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon/>}
                        onClick={() => {
                            refetchJobs();
                            if (selectedJobId) refetchDetail();
                        }}
                    >
                        새로고침
                    </Button>
                </Stack>
            </Stack>

            <Card sx={{mb: 2}}>
                <CardContent>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <FormControl size="small" sx={{minWidth: 220}}>
                            <InputLabel>작업 유형</InputLabel>
                            <Select
                                value={jobType}
                                label="작업 유형"
                                onChange={(e) => {
                                    setJobType(e.target.value);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="DICTIONARY_METADATA_BACKFILL">사전 메타데이터 백필</MenuItem>
                                <MenuItem value="CONTENT_QUALITY_ANALYSIS">콘텐츠 품질 분석</MenuItem>
                                <MenuItem value="CONTENT_QUALITY_APPLY">콘텐츠 품질 보정</MenuItem>
                                <MenuItem value="WORDBOOK_MAGIC_CARD_GENERATION">단어장 매직카드 생성</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{minWidth: 140}}>
                            <InputLabel>스코프</InputLabel>
                            <Select
                                value={scopeType}
                                label="스코프"
                                onChange={(e) => {
                                    setScopeType(e.target.value);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="GLOBAL">전체 범위</MenuItem>
                                <MenuItem value="WORDBOOK">단어장</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="scopeId"
                            value={scopeIdText}
                            onChange={(e) => {
                                setScopeIdText(e.target.value);
                                setPage(0);
                            }}
                        />
                        <FormControl size="small" sx={{minWidth: 140}}>
                            <InputLabel>상태</InputLabel>
                            <Select
                                value={status}
                                label="상태"
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="PENDING">대기</MenuItem>
                                <MenuItem value="RUNNING">진행중</MenuItem>
                                <MenuItem value="AWAITING_RESULTS">결과대기</MenuItem>
                                <MenuItem value="SUCCEEDED">완료</MenuItem>
                                <MenuItem value="FAILED">실패</MenuItem>
                                <MenuItem value="CANCELED">중단</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </CardContent>
            </Card>

            {isLoading && <CircularProgress/>}
            {error && <Alert severity="error">{error.message}</Alert>}

            {data && (
                <Card>
                    <CardContent sx={{p: 0}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>작업 유형</TableCell>
                                    <TableCell>대상</TableCell>
                                    <TableCell>상태</TableCell>
                                    <TableCell>요청/대상/처리</TableCell>
                                    <TableCell>성공/스킵/실패</TableCell>
                                    <TableCell>배치수</TableCell>
                                    <TableCell>진행 상태</TableCell>
                                    <TableCell>시작/종료</TableCell>
                                    <TableCell align="center">작업</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.content ?? []).map((job) => {
                                    const result = parseResultJson(job.resultJson);
                                    return (
                                        <TableRow
                                            key={job.id}
                                            hover
                                            sx={{cursor: 'pointer'}}
                                            onClick={() => setSelectedJobId(job.id)}
                                        >
                                            <TableCell>{job.id}</TableCell>
                                            <TableCell>{batchJobTypeLabel(job.jobType)}</TableCell>
                                            <TableCell>{batchScopeLabel(job)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={batchJobStatusLabel(job.status)} color={batchJobStatusColor(job.status)}/>
                                            </TableCell>
                                            <TableCell>{job.requestedLimit}/{job.targetCount}/{job.processedCount}</TableCell>
                                            <TableCell>{job.successCount}/{job.skippedCount}/{job.failedCount}</TableCell>
                                            <TableCell>{job.batchCount}</TableCell>
                                            <TableCell>
                                                <Typography variant="caption" display="block">
                                                    {result.phase || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {result.currentProvider || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatBatchDateTime(result.heartbeatAt)}
                                                </Typography>
                                                {result.failureSummary && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        P:{result.failureSummary.providerFailedBatches ?? 0}
                                                        {' / '}
                                                        I:{result.failureSummary.internalFailedBatches ?? 0}
                                                        {' / '}
                                                        Retry:{result.failureSummary.retriedWithFallbackBatches ?? 0}
                                                        {' / '}
                                                        Stop:{result.failureSummary.retrySuppressedBatches ?? 0}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" display="block">{formatBatchDateTime(job.startedAt)}</Typography>
                                                <Typography variant="caption" color="text.secondary">{formatBatchDateTime(job.finishedAt)}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {(job.status === 'PENDING' || job.status === 'RUNNING' || job.status === 'AWAITING_RESULTS') && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        disabled={cancelJob.isPending}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!confirm(`배치 작업 #${job.id} 를 중단할까요?`)) return;
                                                            cancelJob.mutate(job.id, {onSuccess: () => refetchJobs()});
                                                        }}
                                                    >
                                                        중단
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {(data.content ?? []).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{py: 3}}>
                                            <Typography variant="body2" color="text.secondary">
                                                조회된 작업이 없습니다.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={data.totalCount ?? 0}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={size}
                            onRowsPerPageChange={(e) => {
                                setSize(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[20, 50, 100]}
                        />
                    </CardContent>
                </Card>
            )}

            <Dialog
                open={!!selectedJobId}
                onClose={() => setSelectedJobId(undefined)}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>배치 상세 이력</DialogTitle>
                <DialogContent>
                    {detailLoading && <CircularProgress />}
                    {detailError && <Alert severity="error">{detailError.message}</Alert>}
                    {detailData && (
                        <BatchJobDetailPanel
                            detail={detailData}
                            onRefresh={refetchDetail}
                            onRefreshList={refetchJobs}
                            submissionPage={detailSubmissionPage}
                            submissionSize={detailSubmissionSize}
                            onSubmissionPageChange={setDetailSubmissionPage}
                            onSubmissionSizeChange={(size) => { setDetailSubmissionSize(size); setDetailSubmissionPage(0); }}
                            generatedPage={detailGeneratedPage}
                            generatedSize={detailGeneratedSize}
                            onGeneratedPageChange={setDetailGeneratedPage}
                            onGeneratedSizeChange={(size) => { setDetailGeneratedSize(size); setDetailGeneratedPage(0); }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};
